<template>
  <v-card flat class="import-widget">
    <v-card-text class="pa-6">
      <div class="d-flex align-center justify-space-between mb-6">
        <div>
          <h3 class="text-h5 font-weight-bold mb-1">匯入特休與請假餘額</h3>
          <p class="text-body-2 text--secondary mb-0">
            請選擇 Excel 檔案以匯入員工的特休與各類請假可用餘額。
          </p>
        </div>
        <div>
          <v-btn
            color="primary"
            class="font-weight-bold text-capitalize bg-gradient-primary mr-2"
            @click="uploadDialog = true"
          >
            <v-icon left size="18">mdi-upload</v-icon>
            匯入檔案
          </v-btn>
          <v-btn
            color="info"
            outlined
            :href="sampleTemplateUrl"
            download
            class="text-capitalize"
          >
            <v-icon left size="18">mdi-download</v-icon>
            下載範本
          </v-btn>
        </div>
      </div>

      <v-divider class="mb-6"></v-divider>

      <v-dialog v-model="uploadDialog" max-width="600px">
        <v-card>
          <v-card-title class="text-h5 grey lighten-2">
            匯入檔案
            <v-spacer></v-spacer>
            <v-btn icon @click="uploadDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="pa-6">
            <div
              class="
                upload-zone
                rounded-lg
                d-flex
                flex-column
                align-center
                justify-center
                py-10
                px-4
                mb-4
              "
              :class="{ dragging: isDragging }"
              @dragover.prevent="isDragging = true"
              @dragleave.prevent="isDragging = false"
              @drop.prevent="handleDrop"
            >
              <v-icon size="64" color="primary" class="mb-4"
                >mdi-file-excel-outline</v-icon
              >
              <div class="text-h6 font-weight-bold mb-2 text-center">
                {{ file ? file.name : "拖曳 Excel 檔案至此或點擊選取" }}
              </div>
              <div class="text-body-2 grey--text text-center mb-6">
                支援 .xlsx, .xls 格式
              </div>

              <v-file-input
                v-model="file"
                accept=".xlsx, .xls"
                label="選擇檔案"
                prepend-icon=""
                prepend-inner-icon="mdi-paperclip"
                outlined
                dense
                class="d-none"
                ref="fileInputRef"
                hide-details
                @change="onFileChange"
              ></v-file-input>

              <v-btn
                color="primary"
                class="px-8 bg-gradient-default"
                large
                @click="triggerFileInput"
                :disabled="loading"
              >
                選取檔案
              </v-btn>
            </div>

            <div v-if="file" class="d-flex align-center justify-center">
              <v-btn
                color="#cb0c9f"
                class="px-10 text-white font-weight-bold"
                large
                :loading="loading"
                @click="uploadFile"
              >
                <v-icon left>mdi-upload</v-icon>
                上傳與驗證
              </v-btn>
              <v-btn
                text
                class="ml-2 grey--text text--darken-1"
                @click="clearFile"
                :disabled="loading"
              >
                取消
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
      </v-dialog>

      <!-- Drafts Table -->
      <div v-if="drafts.length" class="mt-4">
        <div class="d-flex align-center justify-space-between mb-4">
          <h4 class="text-h6 font-weight-bold">待確認匯入資料</h4>
          <div>
            <v-btn
              color="success"
              class="bg-gradient-success mr-2"
              @click="confirmImport"
              :loading="confirming"
              :disabled="!selectedDraft.length"
            >
              確認匯入 ({{ selectedDraft.length }})
            </v-btn>
            <v-btn
              outlined
              color="error"
              @click="resetImport"
              :disabled="confirming"
            >
              重新上傳
            </v-btn>
            <v-btn
              outlined
              color="primary"
              class="ml-2"
              @click="toggleDisplayUnit"
            >
              單位: {{ displayUnit === "hours" ? "小時" : "天" }}
              <v-icon small class="ml-1">mdi-swap-horizontal</v-icon>
            </v-btn>
          </div>
        </div>

        <v-data-table
          :headers="headers"
          :items="drafts"
          :loading="loadingDrafts"
          show-select
          v-model="selectedDraft"
          item-key="id"
          class="elevation-1 rounded-lg overflow-hidden drafts-table"
          :items-per-page="10"
          :footer-props="{
            'items-per-page-options': [10, 20, 50],
          }"
        >
          <template v-slot:item.user.name="{ item }">
            <div class="font-weight-bold text-no-wrap">
              {{ item.user ? item.user.name : "-" }}
            </div>
            <div class="text-caption grey--text">
              {{ item.user ? item.user.mobile_phone : "-" }}
            </div>
          </template>

          <template v-slot:item.personal_leave="{ item }">
            <span class="text-no-wrap">
              {{ formatValue(item.personal_leave) }} /
              {{ formatValue(item.personal_leave_max) }} {{ unitText }}
            </span>
          </template>

          <template v-slot:item.sick_leave="{ item }">
            <span class="text-no-wrap">
              {{ formatValue(item.sick_leave) }} /
              {{ formatValue(item.sick_leave_max) }} {{ unitText }}
            </span>
          </template>

          <template v-slot:item.menstrual_leave="{ item }">
            <span class="text-no-wrap">
              {{ formatValue(item.menstrual_leave) }} /
              {{ formatValue(item.menstrual_leave_max) }} {{ unitText }}
            </span>
          </template>

          <template v-slot:item.occupational_sick_leave="{ item }">
            <span class="text-no-wrap">
              {{ formatValue(item.occupational_sick_leave) }} /
              {{ formatValue(item.occupational_sick_leave_max) }} {{ unitText }}
            </span>
          </template>

          <template v-slot:item.marriage_leave="{ item }">
            <span class="text-no-wrap">
              {{ formatValue(item.marriage_leave) }} /
              {{ formatValue(item.marriage_leave_max) }} {{ unitText }}
            </span>
          </template>

          <template v-slot:item.prenatal_checkup_leave="{ item }">
            <span class="text-no-wrap">
              {{ formatValue(item.prenatal_checkup_leave) }} /
              {{ formatValue(item.prenatal_checkup_leave_max) }} {{ unitText }}
            </span>
          </template>

          <template v-slot:item.maternity_leave="{ item }">
            <span class="text-no-wrap">
              {{ formatValue(item.maternity_leave) }} /
              {{ formatValue(item.maternity_leave_max) }} {{ unitText }}
            </span>
          </template>

          <template v-slot:item.paternity_leave="{ item }">
            <span class="text-no-wrap">
              {{ formatValue(item.paternity_leave) }} /
              {{ formatValue(item.paternity_leave_max) }} {{ unitText }}
            </span>
          </template>

          <template v-slot:item.bereavement_leave="{ item }">
            <span class="text-no-wrap">
              {{ formatValue(item.bereavement_leave) }} /
              {{ formatValue(item.bereavement_leave_max) }} {{ unitText }}
            </span>
          </template>

          <template v-slot:item.official_leave="{ item }">
            <span class="text-no-wrap">
              {{ formatValue(item.official_leave) }} /
              {{ formatValue(item.official_leave_max) }} {{ unitText }}
            </span>
          </template>

          <template v-slot:item.family_care_leave="{ item }">
            <span class="text-no-wrap">
              {{ formatValue(item.family_care_leave) }} /
              {{ formatValue(item.family_care_leave_max) }} {{ unitText }}
            </span>
          </template>

          <template v-slot:item.indigenous_ritual_leave="{ item }">
            <span class="text-no-wrap">
              {{ formatValue(item.indigenous_ritual_leave) }} /
              {{ formatValue(item.indigenous_ritual_leave_max) }} {{ unitText }}
            </span>
          </template>

          <template v-slot:item.compensatory_leave="{ item }">
            <span class="text-no-wrap">
              {{ formatValue(item.compensatory_leave) }} /
              {{ formatValue(item.compensatory_leave_max) }} {{ unitText }}
            </span>
          </template>

          <template v-slot:item.typhoon_leave="{ item }">
            <span class="text-no-wrap">
              {{ formatValue(item.typhoon_leave) }} /
              {{ formatValue(item.typhoon_leave_max) }} {{ unitText }}
            </span>
          </template>

          <template v-slot:item.create_time="{ item }">
            <span class="text-no-wrap">
              {{ formatDateTime(item.create_time) }}
            </span>
          </template>
        </v-data-table>
      </div>

      <!-- Results Alert -->
      <v-alert
        v-if="importResult"
        :type="importResult.success ? 'success' : 'error'"
        outlined
        class="mt-6 mx-auto"
        max-width="800"
        dismissible
      >
        <div class="font-weight-bold red--text">{{ importResult.message }}</div>
        <div v-if="importResult.details" class="mt-2 text-body-2">
          {{ importResult.details }}
        </div>
      </v-alert>
    </v-card-text>
  </v-card>
</template>

<script>
import { convertMinutesToDays, formatDateTime } from "@/utils/time";

export default {
  name: "ImportLeaveBalancesWidget",
  props: {
    selectedCompany: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      uploadDialog: false,
      file: null,
      loading: false,
      isDragging: false,
      importResult: null,
      drafts: [],
      selectedDraft: [],
      loadingDrafts: false,
      confirming: false,
      headers: [
        { text: "員工", value: "user.name", align: "start", fixed: true },
        { text: "事假", value: "personal_leave", align: "center" },
        { text: "病假", value: "sick_leave", align: "center" },
        { text: "生理假", value: "menstrual_leave", align: "center" },
        { text: "公傷病假", value: "occupational_sick_leave", align: "center" },
        { text: "婚假", value: "marriage_leave", align: "center" },
        { text: "產檢假", value: "prenatal_checkup_leave", align: "center" },
        { text: "產假", value: "maternity_leave", align: "center" },
        { text: "陪產檢及陪產假", value: "paternity_leave", align: "center" },
        { text: "喪假", value: "bereavement_leave", align: "center" },
        { text: "公假", value: "official_leave", align: "center" },
        { text: "家庭照顧假", value: "family_care_leave", align: "center" },
        {
          text: "原住民族歲時祭儀假",
          value: "indigenous_ritual_leave",
          align: "center",
        },
        { text: "補休假", value: "compensatory_leave", align: "center" },
        { text: "颱風假", value: "typhoon_leave", align: "center" },
        { text: "建立時間", value: "create_time", align: "end" },
      ],
      displayUnit: localStorage.getItem("leave_duration_unit") || "hours",
    };
  },
  mounted() {
    this.fetchDrafts();
    window.addEventListener(
      "leave-duration-unit-changed",
      this.syncDisplayUnit
    );
  },
  beforeDestroy() {
    window.removeEventListener(
      "leave-duration-unit-changed",
      this.syncDisplayUnit
    );
  },
  watch: {
    selectedCompany: {
      handler() {
        this.fetchDrafts();
        this.resetImport(false);
        this.uploadDialog = false;
      },
      deep: true,
    },
  },
  computed: {
    sampleTemplateUrl() {
      return `${process.env.VUE_APP_API_URL}/resource/samples/leaves-example.xlsx`;
    },
    unitText() {
      return this.displayUnit === "hours" ? "小時" : "天";
    },
  },
  methods: {
    convertMinutesToDays(minutes) {
      if (minutes == null) return "0.0";
      return convertMinutesToDays(minutes);
    },
    syncDisplayUnit() {
      this.displayUnit = localStorage.getItem("leave_duration_unit") || "hours";
    },
    toggleDisplayUnit() {
      this.displayUnit = this.displayUnit === "hours" ? "days" : "hours";
      localStorage.setItem("leave_duration_unit", this.displayUnit);
      window.dispatchEvent(new Event("leave-duration-unit-changed"));
    },
    formatValue(minutes) {
      if (minutes == null) return "0";
      const val = parseFloat(minutes);
      if (isNaN(val)) return "0";
      if (this.displayUnit === "hours") {
        const hours = val / 60;
        return Number.isInteger(hours) ? hours.toString() : hours.toFixed(1);
      }
      const days = val / 480;
      return Number.isInteger(days) ? days.toString() : days.toFixed(1);
    },
    formatDateTime(date) {
      return formatDateTime(date);
    },
    onFileChange(file) {
      if (file) {
        this.file = file;
        this.importResult = null;
      }
    },
    handleDrop(e) {
      this.isDragging = false;
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        const droppedFile = files[0];
        const ext = droppedFile.name.split(".").pop().toLowerCase();
        if (["xlsx", "xls"].includes(ext)) {
          this.file = droppedFile;
          this.importResult = null;
        } else {
          this.$emit("error", {
            title: "格式錯誤",
            message: "僅支援 .xlsx 與 .xls 格式之 Excel 檔案",
          });
        }
      }
    },
    clearFile() {
      this.file = null;
      this.importResult = null;
    },
    triggerFileInput() {
      if (this.$refs.fileInputRef) {
        const input = this.$refs.fileInputRef.$el.querySelector("input");
        if (input) input.click();
      }
    },
    async uploadFile() {
      if (!this.file) return;

      const companyId = this.selectedCompany ? this.selectedCompany.id : null;
      if (!companyId) {
        this.$emit("error", {
          title: "選取錯誤",
          message: "請先選擇所屬公司",
        });
        return;
      }

      this.loading = true;
      this.importResult = null;

      try {
        const formData = new FormData();
        formData.append("file", this.file);

        const response = await this.$axios.post(
          `/api/leave-balances/import-excel?company_id=${companyId}`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        const resData = response.data || {};
        this.importResult = {
          success: true,
          message: resData.message || "匯入資料已暫存",
          details: `成功驗證: ${resData.created || 0} 筆, 跳過: ${
            resData.skipped || 0
          } 筆。請在上方 選擇要匯入的項目後 點擊「確認匯入」。`,
        };

        this.file = null;
        this.uploadDialog = false;

        // Fetch drafts for verification
        await this.fetchDrafts();
      } catch (error) {
        console.error("Import error:", error);
        const errorData = error.response?.data || {};
        this.importResult = {
          success: false,
          message: errorData.message || "匯入失敗",
          details:
            errorData.error ||
            error.message ||
            "請檢查檔案格式與內容是否符合範本",
        };

        this.$emit("error", {
          title: "匯入失敗",
          message: this.importResult.message,
        });
      } finally {
        this.loading = false;
      }
    },
    async fetchDrafts() {
      const companyId = this.selectedCompany ? this.selectedCompany.id : null;
      if (!companyId) return;

      this.loadingDrafts = true;
      try {
        const response = await this.$axios.get(
          `/api/leave-balances/drafts?company_id=${companyId}`
        );
        this.drafts = response.data.data || [];
      } catch (error) {
        console.error("Fetch drafts error:", error);
        this.$emit("error", {
          title: "取得暫存資料失敗",
          message: "無法取得待確認的匯入資料",
        });
      } finally {
        this.loadingDrafts = false;
      }
    },
    async confirmImport() {
      if (!this.drafts.length) return;

      const companyId = this.selectedCompany ? this.selectedCompany.id : null;
      const draftIds = this.selectedDraft.map((d) => d.id);

      this.confirming = true;
      try {
        const response = await this.$axios.post(
          `/api/leave-balances/drafts/approve`,
          {
            company_id: companyId,
            draft_ids: draftIds,
          }
        );

        const results = response.data.results || {};
        const approvedCount = results.approved || 0;

        this.$emit("success", {
          message: "資料已正式匯入",
          details: `已確認更新 ${approvedCount} 筆資料。`,
        });

        this.drafts = [];
        this.importResult = null;
      } catch (error) {
        console.error("Confirm import error:", error);
        this.$emit("error", {
          title: "正式匯入失敗",
          message: error.response?.data?.message || "資料更新失敗",
        });
      } finally {
        this.confirming = false;
      }
    },
    async resetImport(clearServer = true) {
      if (clearServer) {
        const companyId = this.selectedCompany ? this.selectedCompany.id : null;
        if (!companyId) {
          this.drafts = [];
          this.selectedDraft = [];
          this.importResult = null;
          return;
        }

        try {
          await this.$axios.delete(`/api/leave-balances/drafts`, {
            data: {
              company_id: companyId,
            },
          });
        } catch (error) {
          console.error("Clear leave balance drafts error:", error);
          this.$emit("error", {
            title: "清除暫存資料失敗",
            message: error.response?.data?.message || "無法清除匯入草稿",
          });
        }
      }

      this.drafts = [];
      this.selectedDraft = [];
      this.importResult = null;
    },
  },
};
</script>

<style scoped>
.import-widget {
  background-color: transparent !important;
}

.upload-zone {
  border: 2px dashed #e9ecef;
  background-color: #f8f9fa;
  transition: all 0.3s ease;
  cursor: pointer;
}

.upload-zone:hover,
.upload-zone.dragging {
  border-color: #cb0c9f;
  background-color: #fceef8;
}

.bg-gradient-primary {
  background-image: linear-gradient(310deg, #7928ca 0%, #ff0080 100%);
  border: none;
}

.bg-gradient-success {
  background-image: linear-gradient(310deg, #17ad37 0%, #98ec2d 100%);
  border: none;
  color: white !important;
}

.drafts-table >>> th {
  white-space: nowrap;
}
</style>
